import clsx from 'clsx';
import React from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';

interface Props {
  open?: boolean;
  onClose?: () => void;
  title?: string | React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'xxs' | 'sm' | 'lg' | 'xl' | 'xxl' | 'xxxl' | 'full' | 'default';
  customSize?: string;
  classesWrapper?: string;
  zIndex?: string;
}

export const Modal = ({
  open = true,
  onClose,
  title,
  children,
  size = 'default',
  customSize,
  classesWrapper,
  zIndex = 'z-[100]',
}: Props): JSX.Element | null => {
  if (!open) return null;

  const _size = {
    xxs: 'sm:max-w-[350px]',
    sm: 'sm:max-w-[500px]',
    lg: 'sm:max-w-[700px]',
    xl: 'sm:max-w-[900px]',
    xxl: 'sm:max-w-[1100px]',
    xxxl: 'sm:max-w-[96vw]',
    full: 'sm:max-w-full min-h-screen h-screen !rounded-none',
    default: 'sm:max-w-[600px]',
  }[size];

  return createPortal(
    <div
      className={clsx(
        'fixed left-0 top-0 z-[100] flex h-screen w-screen items-center justify-center overflow-hidden bg-gray-500 bg-opacity-50 transition-opacity dark:bg-zinc-950/90',
        zIndex,
      )}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={clsx(
          'flex w-full items-center justify-center text-center sm:block',
          size == 'full' ? 'p-0' : 'px-2',
        )}
      >
        <div
          className={clsx(
            'mx-auto max-h-screen w-full rounded-md bg-white shadow-xl dark:bg-zinc-900',
            customSize || _size,
          )}
        >
          <div className="w-full rounded-md text-start">
            <div className="rounded-md sm:mt-0">
              {!!title && (
                <div className="rounded-t-md border-b pb-1 ps-3 pt-3 dark:border-zinc-700">
                  <div className="flex w-full items-center justify-between">
                    <h3
                      className="text-sm font-medium leading-6 text-zinc-600 first-letter:uppercase dark:text-zinc-300"
                      id="modal-title"
                    >
                      {typeof title === 'string' ? (
                        <span className="capitalize">{String(title).toLocaleLowerCase()}</span>
                      ) : (
                        title
                      )}
                    </h3>
                    {onClose && (
                      <button
                        type="button"
                        className="px-3 py-2 text-zinc-400 hover:text-zinc-500 dark:hover:text-white"
                        onClick={onClose}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="bg-transparent text-sm text-zinc-600 dark:text-zinc-300">
                {!!classesWrapper ? <div className={classesWrapper}>{children}</div> : children}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="t4-tooltip-modal" className="z-50 !bg-slate-800 font-serif !text-xs" />
    </div>,
    document.getElementById('modal')!,
  );
};
